/**
 * Interface pour les options de préchargement
 */
interface PreloadOptions {
  parallel?: number;        // Nombre de chargements parallèles (défaut: 4)
  retries?: number;         // Nombre de tentatives en cas d'échec (défaut: 2)
  timeout?: number;         // Timeout par image en ms (défaut: 30000)
  onProgress?: ProgressCallback; // Callback de progression
  onComplete?: CompleteCallback; // Callback de completion
  onError?: ErrorCallback;   // Callback d'erreur
}

/**
 * Interface pour la progression du préchargement
 */
interface PreloadProgress {
  loaded: number;           // Nombre d'images chargées
  total: number;            // Nombre total d'images
  percentage: number;       // Pourcentage de progression (0-100)
  currentUrl?: string;      // URL en cours de chargement
}

/**
 * Interface pour le résultat d'un préchargement d'image
 */
interface PreloadResult {
  url: string;              // URL de l'image
  success: boolean;         // Si le chargement a réussi
  error?: string;           // Message d'erreur si échec
  duration?: number;        // Durée du chargement en ms
}

/**
 * Interface pour une erreur de préchargement
 */
interface PreloadError {
  url: string;              // URL qui a causé l'erreur
  error: string;            // Message d'erreur
}

/**
 * Types de callbacks
 */
type ProgressCallback = (progress: PreloadProgress) => void;
type CompleteCallback = (results: PreloadResult[]) => void;
type ErrorCallback = (error: PreloadError) => void;

/**
 * Classe ImagePreloader pour précharger des images de manière moderne
 * Supporte le chargement parallèle, les retries, les timeouts et les callbacks
 */
class ImagePreloader {
  private abortController: AbortController | null = null;

  /**
   * Précharge une liste d'images
   * @param urls Liste des URLs d'images à précharger
   * @param options Options de préchargement
   * @returns Promise résolue avec les résultats du préchargement
   */
  async preload(urls: string[], options: PreloadOptions = {}): Promise<PreloadResult[]> {
    const {
      parallel = 4,
      retries = 2,
      timeout = 30000,
      onProgress,
      onComplete,
      onError
    } = options;

    this.abortController = new AbortController();
    const results: PreloadResult[] = [];
    let loaded = 0;

    // Fonction pour charger une image avec retry et timeout
    const loadImage = async (url: string): Promise<PreloadResult> => {
      const startTime = Date.now();

      for (let attempt = 0; attempt <= retries; attempt++) {
        try {
          await this.loadSingleImage(url, timeout);
          const duration = Date.now() - startTime;

          const successResult: PreloadResult = {
            url,
            success: true,
            duration
          };

          loaded++;
          onProgress?.({
            loaded,
            total: urls.length,
            percentage: Math.round((loaded / urls.length) * 100),
            currentUrl: url
          });

          return successResult;
        } catch (error) {
          if (attempt === retries) {
            const errorResult: PreloadResult = {
              url,
              success: false,
              error: error instanceof Error ? error.message : 'Unknown error',
              duration: Date.now() - startTime
            };

            loaded++;
            onProgress?.({
              loaded,
              total: urls.length,
              percentage: Math.round((loaded / urls.length) * 100),
              currentUrl: url
            });

            onError?.({
              url,
              error: error instanceof Error ? error.message : 'Unknown error'
            });

            return errorResult;
          }
          // Attendre un peu avant retry
          await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
        }
      }

      // Ne devrait pas arriver
      return { url, success: false, error: 'Max retries exceeded' };
    };

    // Charger en parallèle par groupes
    const chunks = this.chunkArray(urls, parallel);
    for (const chunk of chunks) {
      if (this.abortController.signal.aborted) {
        break;
      }

      const chunkPromises = chunk.map(url => loadImage(url));
      const chunkResults = await Promise.allSettled(chunkPromises);

      for (const result of chunkResults) {
        if (result.status === 'fulfilled') {
          results.push(result.value);
        } else {
          // En cas d'erreur inattendue
          const failedUrl = chunk[chunkResults.indexOf(result)];
          results.push({
            url: failedUrl,
            success: false,
            error: result.reason?.message || 'Promise rejected'
          });
        }
      }
    }

    onComplete?.(results);
    return results;
  }

  /**
   * Annule le préchargement en cours
   */
  cancel(): void {
    if (this.abortController) {
      this.abortController.abort();
    }
  }

  /**
   * Charge une seule image avec timeout
   */
  private loadSingleImage(url: string, timeout: number): Promise<void> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      let timeoutId: number;

      const cleanup = () => {
        img.onload = null;
        img.onerror = null;
        clearTimeout(timeoutId);
      };

      img.onload = () => {
        cleanup();
        resolve();
      };

      img.onerror = () => {
        cleanup();
        reject(new Error(`Failed to load image: ${url}`));
      };

      // Timeout
      timeoutId = window.setTimeout(() => {
        cleanup();
        reject(new Error(`Timeout loading image: ${url}`));
      }, timeout);

      // Démarrer le chargement
      img.src = url;

      // Gestion de l'annulation
      if (this.abortController) {
        this.abortController.signal.addEventListener('abort', () => {
          cleanup();
          reject(new Error('Preloading cancelled'));
        });
      }
    });
  }

  /**
   * Divise un tableau en chunks
   */
  private chunkArray<T>(array: T[], size: number): T[][] {
    const chunks: T[][] = [];
    for (let i = 0; i < array.length; i += size) {
      chunks.push(array.slice(i, i + size));
    }
    return chunks;
  }
}

export { ImagePreloader, type PreloadOptions, type PreloadProgress, type PreloadResult, type PreloadError };